import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Timestamp, collection, getDocs, orderBy, query, where } from 'firebase/firestore';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { db } from '../../firebase';

interface ImportError {
  type: string;
  source: string;
  message: string;
  voterName?: string;
  timestamp: {
    seconds: number;
    nanoseconds: number;
  };
}

const SocorroImportErrorList: React.FC = () => {
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchImportErrors = async () => {
      try {
        const importErrorsRef = collection(db, 'importErrors');
        const q = query(importErrorsRef, 
          orderBy('timestamp', 'desc'),
          where('timestamp', '>=', Timestamp.fromDate(new Date("4/19/2025 10:00 AM UTC+8"))),
          where('timestamp', '<', Timestamp.fromDate(new Date("4/19/2025 12:00 PM UTC+8"))),
          // where("source", "==", "Socorro Poll Import 2"),
        );
        
        const querySnapshot = await getDocs(q);
        const fetchedErrors = querySnapshot.docs.map(doc => doc.data() as ImportError);
        
        setErrors(fetchedErrors);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching import errors:', err);
        setError('Failed to fetch import errors');
        setLoading(false);
      }
    };

    fetchImportErrors();
  }, []);

  const exportToCSV = () => {
    // Helper function to escape quotes and wrap in quotes
    const escapeCSV = (value: string | number) => {
      const strValue = String(value);
      return `"${strValue.replace(/"/g, '""')}"`;
    };

    // Convert errors to CSV
    const csvContent = [
      // Header
      ['#', 'Type', 'Source', 'Message', 'Voter Name', 'Timestamp'].join(','),
      // Data rows
      ...errors.map((error, index) => [
        index + 1,
        error.type,
        error.source,
        error.message,
        error.voterName || 'N/A',
        new Date(error.timestamp.seconds * 1000).toLocaleString()
      ].map(escapeCSV).join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'import_errors.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2 
        }}
      >
        <Typography variant="h4">
          Socorro Import Errors
        </Typography>
        {errors.length > 0 && (
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<FileDownloadIcon />}
            onClick={exportToCSV}
          >
            Export to CSV
          </Button>
        )}
      </Box>

      {errors.length === 0 ? (
        <Typography variant="body1">
          No import errors found.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Voter Name</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errors.map((error, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{error.type}</TableCell>
                  <TableCell>{error.source}</TableCell>
                  <TableCell>{error.message}</TableCell>
                  <TableCell>{error.voterName || 'N/A'}</TableCell>
                  <TableCell>
                    {new Date(error.timestamp.seconds * 1000).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SocorroImportErrorList;
